import { Manufacturer, Product, ProductVariant, Series } from '../../types/catalogue';
import { RootState } from '../store';
import { CatalogueState } from './catalogue.reducer';

const getCatalogueState = (state: RootState): CatalogueState => state.catalogue;

// ── Primitive selectors ──────────────────────────────────────────────────────

export const getCatalogueManufacturers = (state: RootState): Manufacturer[] => getCatalogueState(state).manufacturers;
export const getCatalogueSelectedVariantId = (state: RootState): string | null =>
  getCatalogueState(state).selectedVariantId;
export const getCatalogueIsLoading = (state: RootState): boolean => getCatalogueState(state).isLoading;
export const getCatalogueIsLoadingProductDetail = (state: RootState): boolean =>
  getCatalogueState(state).isLoadingProductDetail;

// ── Cache selectors (used by saga for smart-fetch) ──────────────────────────

export const isManufacturerSeriesFetched = (state: RootState, id: string): boolean =>
  !!getCatalogueState(state).fetchedIds.manufacturerSeries[id];
export const isSeriesProductsFetched = (state: RootState, id: string): boolean =>
  !!getCatalogueState(state).fetchedIds.seriesProducts[id];
export const isProductDetailFetched = (state: RootState, id: string): boolean =>
  !!getCatalogueState(state).fetchedIds.productDetails[id];
// ── Derived selectors ────────────────────────────────────────────────────────

export const getSelectedManufacturer = (state: RootState): Manufacturer | null => {
  const { manufacturers, selectedManufacturerId } = getCatalogueState(state);
  return manufacturers.find((b) => b.id === selectedManufacturerId) || null;
};

export const getSelectedSeries = (state: RootState): Series | null => {
  const manufacturer = getSelectedManufacturer(state);
  const { selectedSeriesId } = getCatalogueState(state);
  return manufacturer?.series.find((s) => s.id === selectedSeriesId) || null;
};

export const getSelectedProduct = (state: RootState): Product | null => {
  const series = getSelectedSeries(state);
  const { selectedProductId } = getCatalogueState(state);
  return series?.products.find((p) => p.id === selectedProductId) || null;
};

export const getSelectedVariant = (state: RootState): ProductVariant | null => {
  const product = getSelectedProduct(state);
  const { selectedVariantId } = getCatalogueState(state);
  return product?.variants.find((v) => v.id === selectedVariantId) || null;
};
